import { calculateBalance } from './balanceCalculator';
import { Despesa, Participante } from '@/types/app';

// Teste para verificar se a lógica está funcionando corretamente
export function testBalanceCalculator() {
  console.log('=== TESTE: Calculadora de Balanço ===');

  // Participantes de teste
  const participantes: Participante[] = [
    { id: '1', nome: 'João', foto: '', chavePix: undefined },
    { id: '2', nome: 'Maria', foto: '', chavePix: undefined },
    { id: '3', nome: 'Pedro', foto: '', chavePix: undefined }
  ];

  // Despesas de teste - cenário do usuário
  const despesas: Despesa[] = [
    {
      id: '1',
      titulo: 'Despesa A',
      valor: 30,
      data: new Date(),
      categoria: { id: '1', nome: 'Alimentação', emoji: '🍕', cor: '#FF6B6B', icone: '🍕' },
      pagoPorId: '1', // João pagou
      pagoPorNome: 'João',
      divisoes: [
        { usuarioId: '1', valorDevido: 30 } // João deve R$30 (ele mesmo pagou)
      ],
      modoDivisao: 'igual'
    },
    {
      id: '2',
      titulo: 'Despesa B',
      valor: 50,
      data: new Date(),
      categoria: { id: '2', nome: 'Transporte', emoji: '🚗', cor: '#4ECDC4', icone: '🚗' },
      pagoPorId: '2', // Maria pagou
      pagoPorNome: 'Maria',
      divisoes: [
        { usuarioId: '2', valorDevido: 50 } // Maria deve R$50 (ela mesma pagou)
      ],
      modoDivisao: 'igual'
    }
  ];

  console.log('Participantes:', participantes.map(p => ({ id: p.id, nome: p.nome })));
  console.log('Despesas:', despesas.map(d => ({
    titulo: d.titulo,
    valor: d.valor,
    pagoPorId: d.pagoPorId,
    pagoPorNome: d.pagoPorNome,
    divisoes: d.divisoes
  })));

  // Calcular balanço
  const transacoes = calculateBalance(despesas, participantes);

  console.log('\n=== RESULTADO DO TESTE ===');
  console.log('Transações necessárias:', transacoes.length);
  
  if (transacoes.length === 0) {
    console.log('✅ SUCESSO: Não há transações pendentes - cada um pagou suas próprias despesas');
  } else {
    console.log('❌ PROBLEMA: Há transações pendentes quando não deveria haver');
    transacoes.forEach((t, index) => {
      console.log(`${index + 1}. ${t.devedorNome} paga R$ ${t.valor} para ${t.credorNome}`);
    });
  }

  return transacoes;
}

// Teste com divisão igual entre participantes
export function testEqualDivision() {
  console.log('\n=== TESTE: Divisão Igual ===');

  const participantes: Participante[] = [
    { id: '1', nome: 'João', foto: '', chavePix: undefined },
    { id: '2', nome: 'Maria', foto: '', chavePix: undefined },
    { id: '3', nome: 'Pedro', foto: '', chavePix: undefined }
  ];

  const despesas: Despesa[] = [
    {
      id: '1',
      titulo: 'Almoço',
      valor: 90,
      data: new Date(),
      categoria: { id: '1', nome: 'Alimentação', emoji: '🍕', cor: '#FF6B6B', icone: '🍕' },
      pagoPorId: '1', // João pagou tudo
      pagoPorNome: 'João',
      divisoes: [
        { usuarioId: '1', valorDevido: 30 }, // João deve R$30
        { usuarioId: '2', valorDevido: 30 }, // Maria deve R$30
        { usuarioId: '3', valorDevido: 30 }  // Pedro deve R$30
      ],
      modoDivisao: 'igual'
    }
  ];

  const transacoes = calculateBalance(despesas, participantes);

  console.log('Resultado esperado: Maria e Pedro devem R$30 cada para João');
  console.log('Transações:', transacoes);

  const expectedTransactions = 2;
  const actualTransactions = transacoes.length;

  if (actualTransactions === expectedTransactions) {
    console.log('✅ SUCESSO: Número correto de transações');
  } else {
    console.log(`❌ PROBLEMA: Esperado ${expectedTransactions} transações, mas encontrado ${actualTransactions}`);
  }

  return transacoes;
}

// Teste específico para o cenário com muitos participantes (27)
export function testManyParticipants() {
  console.log('\n=== TESTE: Muitos Participantes (27) ===');

  // Criar 27 participantes
  const participantes: Participante[] = [];
  for (let i = 1; i <= 27; i++) {
    participantes.push({
      id: i.toString(),
      nome: `Participante ${i}`,
      foto: '',
      chavePix: undefined
    });
  }

  // Criar despesas onde diferentes pessoas pagam
  const despesas: Despesa[] = [
    {
      id: '1',
      titulo: 'Despesa do Participante 1',
      valor: 100,
      data: new Date(),
      categoria: { id: '1', nome: 'Alimentação', emoji: '🍕', cor: '#FF6B6B', icone: '🍕' },
      pagoPorId: '1', // Participante 1 pagou
      pagoPorNome: 'Participante 1',
      divisoes: [
        { usuarioId: '1', valorDevido: 100 } // Ele mesmo deve R$100
      ],
      modoDivisao: 'igual'
    },
    {
      id: '2',
      titulo: 'Despesa do Participante 2',
      valor: 200,
      data: new Date(),
      categoria: { id: '2', nome: 'Transporte', emoji: '🚗', cor: '#4ECDC4', icone: '🚗' },
      pagoPorId: '2', // Participante 2 pagou
      pagoPorNome: 'Participante 2',
      divisoes: [
        { usuarioId: '2', valorDevido: 200 } // Ele mesmo deve R$200
      ],
      modoDivisao: 'igual'
    },
    {
      id: '3',
      titulo: 'Despesa do Participante 3',
      valor: 150,
      data: new Date(),
      categoria: { id: '3', nome: 'Lazer', emoji: '🎮', cor: '#45B7D1', icone: '🎮' },
      pagoPorId: '3', // Participante 3 pagou
      pagoPorNome: 'Participante 3',
      divisoes: [
        { usuarioId: '3', valorDevido: 150 } // Ele mesmo deve R$150
      ],
      modoDivisao: 'igual'
    }
  ];

  console.log(`Total de participantes: ${participantes.length}`);
  console.log('Despesas:', despesas.map(d => ({
    titulo: d.titulo,
    valor: d.valor,
    pagoPorId: d.pagoPorId,
    pagoPorNome: d.pagoPorNome,
    divisoes: d.divisoes
  })));

  // Calcular balanço
  const transacoes = calculateBalance(despesas, participantes);

  console.log('\n=== RESULTADO DO TESTE COM MUITOS PARTICIPANTES ===');
  console.log('Transações necessárias:', transacoes.length);
  
  if (transacoes.length === 0) {
    console.log('✅ SUCESSO: Não há transações pendentes - cada um pagou suas próprias despesas');
  } else {
    console.log('❌ PROBLEMA: Há transações pendentes quando não deveria haver');
    transacoes.forEach((t, index) => {
      console.log(`${index + 1}. ${t.devedorNome} paga R$ ${t.valor} para ${t.credorNome}`);
    });
  }

  // Verificar se o problema descrito pelo usuário está ocorrendo
  const totalDespesas = despesas.reduce((total, d) => total + d.valor, 0);
  console.log(`\nTotal de todas as despesas: R$ ${totalDespesas}`);
  
  // Verificar se algum participante tem saldo igual ao total de todas as despesas
  const saldos: { [key: string]: number } = {};
  participantes.forEach(p => {
    saldos[p.id] = 0;
  });

  despesas.forEach(despesa => {
    saldos[despesa.pagoPorId] += despesa.valor;
    despesa.divisoes.forEach(divisao => {
      saldos[divisao.usuarioId] -= divisao.valorDevido;
    });
  });

  Object.entries(saldos).forEach(([id, saldo]) => {
    if (Math.abs(saldo) === totalDespesas) {
      const participante = participantes.find(p => p.id === id);
      console.log(`⚠️  ALERTA: ${participante?.nome} tem saldo de R$ ${saldo} (igual ao total de todas as despesas)`);
    }
  });

  return transacoes;
}

// Teste específico para verificar problemas com tipos de dados
export function testDataTypeIssues() {
  console.log('\n=== TESTE: Problemas com Tipos de Dados ===');

  const participantes: Participante[] = [
    { id: '1', nome: 'João', foto: '', chavePix: undefined },
    { id: '2', nome: 'Maria', foto: '', chavePix: undefined }
  ];

  // Teste com diferentes tipos de IDs (string, number, etc.)
  const despesas: Despesa[] = [
    {
      id: '1',
      titulo: 'Despesa com ID string',
      valor: 100,
      data: new Date(),
      categoria: { id: '1', nome: 'Alimentação', emoji: '🍕', cor: '#FF6B6B', icone: '🍕' },
      pagoPorId: '1', // String
      pagoPorNome: 'João',
      divisoes: [
        { usuarioId: '1', valorDevido: 100 }
      ],
      modoDivisao: 'igual'
    },
    {
      id: '2',
      titulo: 'Despesa com ID number',
      valor: 200,
      data: new Date(),
      categoria: { id: '2', nome: 'Transporte', emoji: '🚗', cor: '#4ECDC4', icone: '🚗' },
      pagoPorId: 2 as any, // Number (forçado)
      pagoPorNome: 'Maria',
      divisoes: [
        { usuarioId: 2 as any, valorDevido: 200 } // Number (forçado)
      ],
      modoDivisao: 'igual'
    }
  ];

  console.log('Testando com IDs de tipos diferentes:');
  console.log('Despesa 1 - pagoPorId:', despesas[0].pagoPorId, 'tipo:', typeof despesas[0].pagoPorId);
  console.log('Despesa 2 - pagoPorId:', despesas[1].pagoPorId, 'tipo:', typeof despesas[1].pagoPorId);

  const transacoes = calculateBalance(despesas, participantes);

  console.log('\n=== RESULTADO DO TESTE DE TIPOS ===');
  console.log('Transações necessárias:', transacoes.length);
  
  if (transacoes.length === 0) {
    console.log('✅ SUCESSO: Não há transações pendentes - tipos de dados funcionando corretamente');
  } else {
    console.log('❌ PROBLEMA: Há transações pendentes - possível problema com tipos de dados');
    transacoes.forEach((t, index) => {
      console.log(`${index + 1}. ${t.devedorNome} paga R$ ${t.valor} para ${t.credorNome}`);
    });
  }

  return transacoes;
} 